import { useEffect, useState } from "react";
import { AlertTriangle, MapPin, Plus, Save, Trash2, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import type { School } from "@/types/database";

export default function AdminSchoolsPage() {
  const [rows, setRows] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Record<string, Partial<School>>>({});
  const [adding, setAdding] = useState<Partial<School> | null>(null);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from("schools").select("*").order("name");
    setRows((data ?? []) as School[]);
    setLoading(false);
  }
  useEffect(() => { void load(); }, []);

  function update(id: string, key: keyof School, val: unknown) {
    setEditing((prev) => ({ ...prev, [id]: { ...prev[id], [key]: val } }));
  }

  async function save(id: string) {
    const patch = editing[id];
    if (!patch) return;
    const { latitude, longitude, ...rest } = patch;
    const toNum = (v: unknown): number | null => {
      if (v == null) return null;
      const s = String(v).trim();
      if (s === "") return null;
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    };
    const cleanLat = toNum(latitude);
    const cleanLon = toNum(longitude);
    await supabase.from("schools").update({ ...rest, latitude: cleanLat, longitude: cleanLon }).eq("id", id);
    setEditing((prev) => { const n = { ...prev }; delete n[id]; return n; });
    void load();
  }

  async function addNew() {
    if (!adding?.name) return;
    await supabase.from("schools").insert({
      name: adding.name,
      city: adding.city ?? null,
      county: adding.county ?? null,
      latitude: adding.latitude == null ? null : Number(adding.latitude),
      longitude: adding.longitude == null ? null : Number(adding.longitude),
      pending: false,
    });
    setAdding(null);
    void load();
  }

  async function remove(id: string) {
    if (!confirm("Remove this school?")) return;
    await supabase.from("schools").delete().eq("id", id);
    void load();
  }

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl text-white">Texas schools</h1>
          <p className="text-steel-300">Edit names, cities, and coordinates. Schools without lat/long don't appear on the map.</p>
        </div>
        <button onClick={() => setAdding({ name: "" })} className="btn-primary"><Plus className="w-4 h-4" /> Add school</button>
      </header>

      {adding && (
        <div className="card-dark p-4 grid sm:grid-cols-5 gap-2">
          <input className="input" placeholder="Name"  value={adding.name ?? ""} onChange={(e) => setAdding({ ...adding, name: e.target.value })} />
          <input className="input" placeholder="City"  value={adding.city ?? ""} onChange={(e) => setAdding({ ...adding, city: e.target.value })} />
          <input className="input" placeholder="County" value={adding.county ?? ""} onChange={(e) => setAdding({ ...adding, county: e.target.value })} />
          <input className="input" placeholder="Latitude" value={adding.latitude ?? ""} onChange={(e) => setAdding({ ...adding, latitude: e.target.value as unknown as number })} />
          <input className="input" placeholder="Longitude" value={adding.longitude ?? ""} onChange={(e) => setAdding({ ...adding, longitude: e.target.value as unknown as number })} />
          <div className="sm:col-span-5 flex gap-2 justify-end">
            <button onClick={() => setAdding(null)} className="btn-outline">Cancel</button>
            <button onClick={addNew} className="btn-primary"><Save className="w-4 h-4" /> Save</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-steel-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="card-dark overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-steel-900/60 text-steel-300 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">City</th>
                <th className="text-left p-3">County</th>
                <th className="text-left p-3">Latitude</th>
                <th className="text-left p-3">Longitude</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-steel-700">
              {rows.map((s) => {
                const eff = <K extends keyof School>(k: K): School[K] | undefined =>
                  (editing[s.id]?.[k] as School[K] | undefined) ?? s[k];
                const noCoords = s.latitude == null || s.longitude == null;
                return (
                  <tr key={s.id} className={noCoords ? "bg-amber-500/5" : ""}>
                    <td className="p-2"><input className="input py-1" value={String(eff("name") ?? "")} onChange={(e) => update(s.id, "name", e.target.value)} /></td>
                    <td className="p-2"><input className="input py-1" value={String(eff("city") ?? "")} onChange={(e) => update(s.id, "city", e.target.value)} /></td>
                    <td className="p-2"><input className="input py-1" value={String(eff("county") ?? "")} onChange={(e) => update(s.id, "county", e.target.value)} /></td>
                    <td className="p-2"><input className="input py-1" value={String(eff("latitude") ?? "")} onChange={(e) => update(s.id, "latitude", e.target.value as unknown as number)} /></td>
                    <td className="p-2"><input className="input py-1" value={String(eff("longitude") ?? "")} onChange={(e) => update(s.id, "longitude", e.target.value as unknown as number)} /></td>
                    <td className="p-2 text-right whitespace-nowrap space-x-2">
                      {noCoords && <span className="pill-warn"><AlertTriangle className="w-3 h-3" /> no map</span>}
                      <button onClick={() => save(s.id)} className="text-forge-300 hover:underline inline-flex items-center gap-1"><Save className="w-4 h-4" />Save</button>
                      <button onClick={() => remove(s.id)} className="text-rose-400 hover:underline inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-steel-400">No schools yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-steel-400 inline-flex items-center gap-1">
        <MapPin className="w-3 h-3" /> Default Texas map center is 31.9686, -99.9018, zoom 6.
      </p>
    </div>
  );
}
